import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, LogIn, User, Lock, AlertCircle } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();

  const [nombre, setNombre] = useState("");
  const [clave, setClave] = useState("");
  const [errorMensaje, setErrorMensaje] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Feedback visual extra

  const handleNombreChange = (e) => {
    setNombre(e.target.value.trim());
    setErrorMensaje("");
  };

  const handleClaveChange = (e) => {
    setClave(e.target.value.trim());
    setErrorMensaje("");
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!nombre) return setErrorMensaje("El usuario es obligatorio");
    if (!clave) return setErrorMensaje("La contraseña es obligatoria");

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/login`,
        { nombre, clave }
      );

      if (response.status === 200) {
        const { token, usuario } = response.data;
        localStorage.setItem("auth_usuario", token);
        localStorage.setItem("userName", usuario.nombre);
        navigate("/layout");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setErrorMensaje(error.response.data.message);
      } else {
        setErrorMensaje("Error de conexión con el servidor");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#f8fafc] font-sans antialiased text-slate-900">
      {/* Decoración de fondo minimalista */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-slate-900 clip-path-slant z-0 md:block hidden"></div>

      <div className="relative z-10 w-full max-w-sm px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-100">

          {/* Cabecera de la Tarjeta */}
          <div className="bg-white pt-7 pb-5 px-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 mb-3 border border-emerald-100">
              <LogIn size={24} />
            </div>
            <div className="space-y-1">
              <span className="block text-[10px] font-bold tracking-[0.2em] text-emerald-600 uppercase">
                Plataforma Integral
              </span>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">
                Gestión de <span className="text-emerald-600">Tambos</span>
              </h1>
              <p className="text-slate-500 text-xs font-medium">Servicios de precisión operativa</p>
            </div>
          </div>

          {/* Cuerpo del Formulario */}
          <form onSubmit={handleLogin} className="p-6 pt-0 space-y-4">

            {/* Input Usuario */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-1">
                Usuario
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-emerald-500 transition-colors">
                  <User size={16} />
                </div>
                <input
                  type="text"
                  value={nombre}
                  onChange={handleNombreChange}
                  placeholder="ej. juan.perez"
                  className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all placeholder:text-slate-400"
                />
              </div>
            </div>

            {/* Input Contraseña */}
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider ml-1">
                Contraseña
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-emerald-500 transition-colors">
                  <Lock size={16} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={clave}
                  onChange={handleClaveChange}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-12 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all placeholder:text-slate-400"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-md transition-all"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Alerta de Error */}
            {errorMensaje && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 px-3 py-1.5 rounded-lg text-xs font-medium animate-in fade-in slide-in-from-top-1">
                <AlertCircle size={16} />
                {errorMensaje}
              </div>
            )}

            {/* Botón de Acción */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold py-2.5 rounded-lg shadow-lg shadow-slate-200 transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-out"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={18} />
                  ACCEDER AL PANEL
                </>
              )}
            </button>
          </form>

          {/* Footer de la Tarjeta */}
          {/* <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400 uppercase tracking-widest font-medium">
              Software de FCDesarrollo
            </p>
          </div> */}
        </div>
      </div>

      {/* Estilo CSS extra para el fondo (opcional) */}
      <style>{`
        .clip-path-slant {
          clip-path: polygon(0 0, 100% 0, 100% 70%, 0% 100%);
        }
      `}</style>
    </div>
  );
}